<script setup lang="ts">
import type { ExtendedLocationDetail } from '@/types/location';

const props = defineProps({
	disabled: {
		type: Boolean,
		default: false,
	},
	placeholder: {
		type: String,
		default: '',
	},
	errorMessage: {
		type: String,
		default: undefined,
	},
	modelValue: {
		type: String,
		default: '',
	},
	options: {
		type: Object as PropType<google.maps.places.AutocompleteOptions | null>,
		default: null,
	},
	isSearchBar: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
	(e: 'onSelectLocation', data: ExtendedLocationDetail): void
	(e: 'onInputChange'): void
	(e: 'focus'): void
	(e: 'blur'): void
	(e: 'enter'): void
}>();

const { autocomplete, initGeocoder, initAutocomplete, fetchLocation } = useGoogleMaps();

const input = ref();
const activator = ref();
const tooltipContent = ref();

const textInput = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

const isShowTooltip = computed(() => {
	return !!activator.value;
});

function formatAddress(address: string): string {
	// Match patterns like 'NY 11735' and remove just the postal code, keeping state and country
	return address.replace(/([A-Z]{2})\s*\d{5}(-\d{4})?/i, '$1');
}

async function initialize() {
	if (!autocomplete.value) {
		return;
	}

	autocomplete.value.addListener('place_changed', async () => {
		if (!autocomplete.value) {
			return;
		}
		const places = autocomplete.value.getPlace();
		if (places && places.formatted_address) {
			const { countryCode } = await fetchLocation(places.formatted_address);
			const formattedAddress = formatAddress(places.formatted_address);

			const location: ExtendedLocationDetail = {
				name: places?.name || '',
				address: formattedAddress || '',
				coordinate: {
					lat: places.geometry?.location?.lat() || null,
					lng: places.geometry?.location?.lng() || null,
				},
				countryCode: countryCode || '',
			};
			emit('onSelectLocation', location);
		}
	});
}

function handleOnFocus() {
	const el = document.querySelector('.pac-container');
	if (!el) {
		return;
	}

	if (props.isSearchBar) {
		el.classList.add('-search-bar');
	}

	el?.addEventListener('mouseover', (e) => {
		const target = e.target;

		if (!(target instanceof HTMLElement)) {
			return;
		}

		if (target.className === 'pac-item') {
			activator.value = target;
			tooltipContent.value = target.innerText;
		} else {
			const parent = target.closest('.pac-item');
			if (!(parent instanceof HTMLElement)) {
				return;
			}

			activator.value = parent;
			tooltipContent.value = parent.innerText;
		}
	});

	emit('focus');
}

function handleOnBlur() {
	activator.value = null;
	tooltipContent.value = null;
	emit('blur');
}

function handlePressEnter(event: Event) {
	event.preventDefault();
	emit('enter');
}

onMounted(async () => {
	try {
		// Ensure Google Maps is loaded before initialization
		await initGoogleMaps();
		await initGeocoder();
		// Only initialize if input is available
		if (input.value && input.value.input) {
			await initAutocomplete(input.value.input, props.options);
			initialize();
		}
	} catch (error) {
		// Silently ignore Google Maps timeout errors (benign third-party issue)
		if (error === 'Timeout' || (error instanceof Error && error.message === 'Timeout')) {
			return;
		}
		// Log other genuine errors
		console.error('Failed to initialize Google Maps Autocomplete:', error);
	}
});
</script>

<template>
  <BaseTextInput
    ref="input"
    v-model="textInput"
    :placeholder="placeholder"
    :error-message="errorMessage"
    :disabled="disabled"
    @change="$emit('onInputChange')"
    @focus="handleOnFocus"
    @blur="handleOnBlur"
    @keydown.enter="handlePressEnter"
  >
    <template #leftIcon>
      <slot name="icon" />
    </template>
  </BaseTextInput>

  <VTooltip
    v-if="isShowTooltip"
    :activator="activator"
    max-width="200"
    offset="0 80"
    location="bottom right"
  >
    <SafeHtml
      class="tooltip"
      :html-content="tooltipContent"
    />
  </VTooltip>
</template>
